<template>
  <div>
    <!-- Header -->
    <div class="d-flex align-items-center justify-content-between mb-3">
      <div>
        <div style="font-size:14px;font-weight:700;color:#2d2b55;">
          Plantillas de mensaje
        </div>
        <div style="font-size:12px;color:#6b7280;">
          Meta requiere aprobar cada plantilla antes de usarla (24-48h).
        </div>
      </div>
      <el-button
        size="small"
        icon="el-icon-plus"
        style="background:#2d2b55;color:#fff;border:none;border-radius:8px;"
        @click="
          showForm = true;
          editingTemplate = null;
        "
      >
        Nueva plantilla
      </el-button>
    </div>

    <!-- Formulario inline -->
    <div v-if="showForm" class="wa-form-wrapper mb-4">
      <WhatsappTemplateForm
        :template="editingTemplate"
        @saved="onSaved"
        @cancel="
          showForm = false;
          editingTemplate = null;
        "
      />
    </div>

    <!-- Estado vacío -->
    <div v-if="!loading && !templates.length && !showForm" class="wa-empty">
      <div style="font-size:32px;">📋</div>
      <div class="mt-2" style="font-weight:600;color:#2d2b55;">
        Sin plantillas
      </div>
      <div style="font-size:13px;color:#6b7280;margin-top:4px;">
        Crea tu primera plantilla para empezar a enviar mensajes por WhatsApp.
      </div>
    </div>

    <!-- Lista -->
    <div v-if="loading" class="text-center py-4">
      <i class="el-icon-loading" style="font-size:24px;color:#3ecfb4;"></i>
    </div>

    <div v-for="tpl in templates" :key="tpl.id" class="wa-tpl-card mb-3">
      <div class="wa-tpl-card__header">
        <div class="wa-tpl-card__name">{{ tpl.name }}</div>
        <div class="d-flex align-items-center" style="gap:8px;">
          <span class="wa-tpl-category">{{ categoryLabel(tpl.category) }}</span>
          <WhatsappStatusBadge :status="tpl.status" />
        </div>
      </div>

      <div class="wa-tpl-card__body mt-2">{{ tpl.body }}</div>

      <div
        v-if="tpl.status === 'rejected' && tpl.rejection_reason"
        class="wa-alert--error mt-2 px-2 py-1"
      >
        <i class="el-icon-warning-outline mr-1"></i> {{ tpl.rejection_reason }}
      </div>

      <div class="wa-tpl-card__actions mt-3">
        <!-- Editar: solo draft/rejected -->
        <el-button
          v-if="tpl.status === 'draft' || tpl.status === 'rejected'"
          size="mini"
          icon="el-icon-edit"
          @click="editTemplate(tpl)"
        >
          Editar
        </el-button>

        <!-- Enviar a aprobación: solo draft/rejected -->
        <el-button
          v-if="tpl.status === 'draft' || tpl.status === 'rejected'"
          size="mini"
          icon="el-icon-upload"
          :loading="submitting === tpl.id"
          style="background:#3ecfb4;color:#fff;border:none;"
          @click="submit(tpl)"
        >
          Enviar a Meta
        </el-button>

        <!-- Sincronizar: solo submitted -->
        <el-button
          v-if="tpl.status === 'submitted'"
          size="mini"
          icon="el-icon-refresh"
          :loading="syncing === tpl.id"
          @click="sync(tpl)"
        >
          Sincronizar estado
        </el-button>

        <!-- Eliminar: siempre -->
        <el-button
          size="mini"
          icon="el-icon-delete"
          type="danger"
          plain
          :loading="deleting === tpl.id"
          @click="remove(tpl)"
        >
          Eliminar
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import configService from "../../../services/configService";
import WhatsappStatusBadge from "./WhatsappStatusBadge";
import WhatsappTemplateForm from "./WhatsappTemplateForm";

const CATEGORY_LABELS = {
  utility: "Transaccional",
  marketing: "Marketing",
  authentication: "Autenticación"
};

export default {
  name: "WhatsappTemplateList",
  components: { WhatsappStatusBadge, WhatsappTemplateForm },
  data() {
    return {
      templates: [],
      loading: false,
      showForm: false,
      editingTemplate: null,
      submitting: null,
      syncing: null,
      deleting: null
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await configService.getWhatsAppTemplates();
        if (res.data.success) this.templates = res.data.data;
      } finally {
        this.loading = false;
      }
    },

    editTemplate(tpl) {
      this.editingTemplate = tpl;
      this.showForm = true;
    },

    onSaved(tpl) {
      this.showForm = false;
      this.editingTemplate = null;
      const i = this.templates.findIndex(t => t.id === tpl.id);
      if (i >= 0) this.templates.splice(i, 1, tpl);
      else this.templates.unshift(tpl);
    },

    async submit(tpl) {
      this.submitting = tpl.id;
      try {
        const res = await configService.submitWhatsAppTemplate(tpl.id);
        if (res.data.success) {
          this.$notifications.success({
            message: "Plantilla enviada a Meta para aprobación."
          });
          const i = this.templates.findIndex(t => t.id === tpl.id);
          if (i >= 0) this.templates.splice(i, 1, res.data.data);
        } else {
          this.$notifications.error({ message: res.data.message });
        }
      } finally {
        this.submitting = null;
      }
    },

    async sync(tpl) {
      this.syncing = tpl.id;
      try {
        const res = await configService.syncWhatsAppTemplate(tpl.id);
        if (res.data.success) {
          this.$notifications.success({ message: "Estado sincronizado." });
          const i = this.templates.findIndex(t => t.id === tpl.id);
          if (i >= 0) this.templates.splice(i, 1, res.data.data);
        }
      } finally {
        this.syncing = null;
      }
    },

    async remove(tpl) {
      try {
        await this.$confirm(
          `¿Eliminar la plantilla "${tpl.name}"?${
            tpl.meta_template_id ? " También se eliminará en Meta." : ""
          }`,
          "Eliminar plantilla",
          {
            confirmButtonText: "Eliminar",
            cancelButtonText: "Cancelar",
            type: "warning"
          }
        );
      } catch {
        return;
      }

      this.deleting = tpl.id;
      try {
        const res = await configService.deleteWhatsAppTemplate(tpl.id);
        if (res.data.success) {
          this.templates = this.templates.filter(t => t.id !== tpl.id);
          this.$notifications.success({ message: "Plantilla eliminada." });
        }
      } finally {
        this.deleting = null;
      }
    },

    categoryLabel(cat) {
      return CATEGORY_LABELS[cat] || cat;
    }
  }
};
</script>

<style scoped>
.wa-tpl-card {
  border: 0.5px solid #e8e7e3;
  border-radius: 10px;
  padding: 14px 16px;
  background: #fff;
}
.wa-tpl-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}
.wa-tpl-card__name {
  font-weight: 700;
  font-size: 13px;
  color: #2d2b55;
}
.wa-tpl-card__body {
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
}
.wa-tpl-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.wa-tpl-category {
  font-size: 11px;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 6px;
  padding: 2px 8px;
  font-weight: 500;
}
.wa-form-wrapper {
  border: 0.5px solid #e8e7e3;
  border-radius: 10px;
  padding: 16px;
  background: #fafafa;
}
.wa-empty {
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;
}
.wa-alert--error {
  background: #fee2e2;
  color: #991b1b;
  border-radius: 6px;
  font-size: 12px;
}
</style>
